import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import LandingPage from './pages/landing-page';
import SignInPage from './pages/sign-in-page';
import SignUpPage from './pages/sign-up-page';
import SearchPage from './pages/search-page';
import LibraryPage from './pages/library-page';
import NotificationsPage from './pages/notifications-page';
import ProfileHomePage from './pages/profile/profile-home-page';
import ProfileRedirect from './pages/profile/profile-redirect';
import BlogHomePage from './pages/blog/blog-home-page';
import BlogPostPage from './pages/blog/blog-post-page';
import CreateBlogPage from './pages/blog/create-blog-page';
import TopicPage from './pages/blog/topic-page';
import ForumHomePage from './pages/forum/forum-home-page';
import ForumDiscussionPage from './pages/forum/forum-discussion-page';
import CategoryPage from './pages/forum/category-page';
import CreateDiscussionPage from './pages/forum/create-discussion-page';
import CreateCommunityPage from './pages/forum/create-community-page';
import BadgesPage from './pages/forum/badges-page';
import CommunitySelectorPage from './pages/forum/community-selector-page';
import LearnMorePage from './pages/learn-more-page';
import CompleteRegistrationPage from './pages/profile/complete-registration-page';
import NotFoundPage from './pages/not-found-page';
import AboutPage from './pages/about-page';
import FeaturesPage from './pages/features-page';
import DiscoverPage from './pages/discover-page';
import EnterprisePage from './pages/enterprise-page';
import PricingPage from './pages/pricing-page';
import GetStartedPage from './pages/get-started-page';
import GateTestPage from './pages/gate-test-page';
// Admin pages
import AdminLayout from './pages/admin/admin-layout';
import AdminDashboard from './pages/admin/admin-dashboard';
import AdminUsers from './pages/admin/admin-users';
import AdminContent from './pages/admin/admin-content';
import AdminReports from './pages/admin/admin-reports';
import AdminAuditLogs from './pages/admin/admin-audit-logs';
import AdminNodeHealth from './pages/admin/admin-node-health';
import AdminCommunities from './pages/admin/admin-communities';
import AdminAppeals from './pages/admin/admin-appeals';
import MyAppeals from './pages/my-appeals';
// Community Admin pages
import CommunityAdminLayout from './pages/forum/community-admin-layout';
import CommunityAdminDashboard from './pages/forum/community-admin-dashboard';
import CommunityAdminMembers from './pages/forum/community-admin-members';
import CommunityAdminDiscussions from './pages/forum/community-admin-discussions';
import CommunityAdminBanned from './pages/forum/community-admin-banned';
import CommunityAdminSettings from './pages/forum/community-admin-settings';
import CommunityAdminActivity from './pages/forum/community-admin-activity';
import CommunityAdminReports from './pages/forum/community-admin-reports';
import { ThemeProvider } from './components/providers/theme-provider';
// AuthProvider is now in web3-provider.jsx
import { Toaster } from './components/ui/sonner';
import ScrollToTop from './components/layout/scroll-to-top';
import ErrorBoundary from './components/providers/error-boundary';
import {
  ProtectedRoute,
  UnverifiedOnlyRoute,
  PublicOnlyRoute,
} from './components/providers/route-protection';
import WalletListener from './components/listeners/WalletListener';
import PassportListener from './components/listeners/PassportListener';

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light" storageKey="chill-zone-theme">
        <Router
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/enterprise" element={<EnterprisePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/get-started" element={<GetStartedPage />} />
            <Route path="/gate-test" element={<GateTestPage />} />
            <Route
              path="/sign-in"
              element={
                <PublicOnlyRoute>
                  <SignInPage />
                </PublicOnlyRoute>
              }
            />
            <Route
              path="/sign-up"
              element={
                <PublicOnlyRoute>
                  <SignUpPage />
                </PublicOnlyRoute>
              }
            />
            <Route path="/learn" element={<LearnMorePage />} />
            <Route path="/learn-more" element={<LearnMorePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-appeals"
              element={
                <ProtectedRoute>
                  <MyAppeals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfileRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/:userId"
              element={
                <ProtectedRoute>
                  <ProfileHomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blog"
              element={
                <ProtectedRoute>
                  <BlogHomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blog/post/:postId"
              element={
                <ProtectedRoute requireVerified={true}>
                  <BlogPostPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blog/new"
              element={
                <ProtectedRoute requireVerified={true}>
                  <CreateBlogPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blog/create"
              element={
                <ProtectedRoute requireVerified={true}>
                  <CreateBlogPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blog/topic/:topicSlug"
              element={
                <ProtectedRoute requireVerified={true}>
                  <TopicPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forum"
              element={
                <ProtectedRoute>
                  <CommunitySelectorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forum/create"
              element={
                <ProtectedRoute requireVerified={true}>
                  <CreateCommunityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forum/:communitySlug"
              element={
                <ProtectedRoute>
                  <ForumHomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forum/:communitySlug/discussion/:discussionId"
              element={
                <ProtectedRoute requireVerified={true}>
                  <ForumDiscussionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forum/:communitySlug/category/:categorySlug"
              element={
                <ProtectedRoute requireVerified={true}>
                  <CategoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forum/:communitySlug/discussion/create"
              element={
                <ProtectedRoute requireVerified={true}>
                  <CreateDiscussionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forum/:communitySlug/badges"
              element={
                <ProtectedRoute>
                  <BadgesPage />
                </ProtectedRoute>
              }
            />
            {/* Community Admin Routes */}
            <Route
              path="/forum/:communitySlug/admin"
              element={
                <ProtectedRoute>
                  <CommunityAdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<CommunityAdminDashboard />} />
              <Route path="members" element={<CommunityAdminMembers />} />
              <Route
                path="discussions"
                element={<CommunityAdminDiscussions />}
              />
              <Route path="reports" element={<CommunityAdminReports />} />
              <Route path="banned" element={<CommunityAdminBanned />} />
              <Route path="settings" element={<CommunityAdminSettings />} />
              <Route path="activity" element={<CommunityAdminActivity />} />
            </Route>
            {/* Legacy routes for backwards compatibility */}
            <Route
              path="/forum/discussion/:discussionId"
              element={
                <ProtectedRoute requireVerified={true}>
                  <ForumDiscussionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forum/category/:categorySlug"
              element={
                <ProtectedRoute requireVerified={true}>
                  <CategoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forum/discussion/create"
              element={
                <ProtectedRoute requireVerified={true}>
                  <CreateDiscussionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forum/badges"
              element={
                <ProtectedRoute>
                  <BadgesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/complete-registration"
              element={
                <UnverifiedOnlyRoute>
                  <CompleteRegistrationPage />
                </UnverifiedOnlyRoute>
              }
            />
            {/* Admin Routes - AdminLayout handles its own auth/authorization */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="communities" element={<AdminCommunities />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="appeals" element={<AdminAppeals />} />
              <Route path="node-health" element={<AdminNodeHealth />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <PassportListener />
          <WalletListener />
          <ScrollToTop />
        </Router>
        <Toaster />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
